import React, { useState, useRef, useLayoutEffect, useMemo } from "react";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import API_BASE_URL from "../apiConfig";

const MM_TO_PX = 3.7795275591; // CSS mm -> px at 96dpi
const PAGE_H_MM = 297;
const PAGE_PAD_MM = 15; // top+bottom padding, matches .invoice-page CSS
const CONTENT_H_PX = (PAGE_H_MM - PAGE_PAD_MM * 2) * MM_TO_PX;

// ---- simple Indian-style number to words ----
function numberToWords(num) {
  const a = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen",
    "Seventeen", "Eighteen", "Nineteen"];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  function two(n) {
    if (n < 20) return a[n];
    return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
  }
  function three(n) {
    if (n > 99) return a[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + two(n % 100) : "");
    return two(n);
  }
  let n = Math.round(num);
  if (n === 0) return "Zero";
  let str = "";
  const crore = Math.floor(n / 10000000); n %= 10000000;
  const lakh = Math.floor(n / 100000); n %= 100000;
  const thousand = Math.floor(n / 1000); n %= 1000;
  const hundred = n;
  if (crore) str += three(crore) + " Crore ";
  if (lakh) str += three(lakh) + " Lakh ";
  if (thousand) str += three(thousand) + " Thousand ";
  if (hundred) str += three(hundred);
  return str.trim();
}

// ---- shared sub-components ----
function HeaderBlock({ d }) {
  return (
    <div className={`invoice-header ${d.useLetterhead ? "blank" : ""}`}>
      {!d.useLetterhead && (
        <>
          <div className="invoice-company">
            <h1>{d.companyName}</h1>
            <p>{[d.companyAddress, d.companyCity].filter(Boolean).join(", ")}</p>
            {d.companyGstin && <p>GSTIN: {d.companyGstin}</p>}
            {d.companyPhone && <p>Phone: {d.companyPhone}</p>}
          </div>
          <div className="invoice-title">
            <h2>TAX INVOICE</h2>
            <p>Invoice No: {d.invoiceNo}</p>
            <p>Date: {d.txnDate?.slice(0, 10)}</p>
          </div>
        </>
      )}
      {d.useLetterhead && (
        <div className="invoice-title" style={{ width: "100%", textAlign: "right" }}>
          <p>Invoice No: {d.invoiceNo}</p>
          <p>Date: {d.txnDate?.slice(0, 10)}</p>
        </div>
      )}
    </div>
  );
}

function MetaBlock({ d }) {
  return (
    <div className="invoice-meta-grid">
      <div className="invoice-meta-box">
        <strong>Billed To</strong>
        {d.partyName}<br />
        {[d.address, d.city].filter(Boolean).join(", ")}<br />
        GSTIN: {d.gstin ?? "-"}
      </div>
      <div className="invoice-meta-box">
        <strong>Invoice Details</strong>
        Invoice No: {d.invoiceNo}<br />
        Date: {d.txnDate?.slice(0, 10)}
      </div>
    </div>
  );
}

function TableHead() {
  return (
    <thead>
      <tr>
        <th style={{ width: "6%" }}>Sr</th>
        <th style={{ width: "34%" }}>Particulars</th>
        <th style={{ width: "12%" }}>HSN</th>
        <th style={{ width: "10%" }}>GST%</th>
        <th style={{ width: "12%" }} className="num">Qty</th>
        <th style={{ width: "13%" }} className="num">Rate</th>
        <th style={{ width: "13%" }} className="num">Amt</th>
      </tr>
    </thead>
  );
}

function ItemRow({ item, sr }) {
  return (
    <tr>
      <td>{sr}</td>
      <td>{item.productName}</td>
      <td>{item.hsn ?? ""}</td>
      <td className="num">{item.gstPercent}</td>
      <td className="num">{item.qty.toFixed(3)}</td>
      <td className="num">{item.rate.toFixed(2)}</td>
      <td className="num">{item.amount.toFixed(2)}</td>
    </tr>
  );
}

function FooterBlock({ d }) {
  return (
    <div className="invoice-footer-block">
      <table className="tax-table">
        <tbody>
          <tr>
            <td>Total Qty</td>
            <td className="num">{d.totalQty.toFixed(3)}</td>
          </tr>
          <tr>
            <td>Total Gross Amt</td>
            <td className="num">{d.taxableAmount.toFixed(2)}</td>
          </tr>
          {d.taxBreakup.map((t, i) => (
            <tr key={i}>
              <td>{t.label} on {t.on.toFixed(2)}</td>
              <td className="num">{t.amount.toFixed(2)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="invoice-net">Net Amt: {d.grandTotal.toFixed(2)}</p>
      <p className="invoice-words">
        Rupees {numberToWords(d.grandTotal)} only. E.&amp;O.E.
      </p>
      {d.remarks && (
        <p className="invoice-terms"><strong>Terms:</strong> {d.remarks}</p>
      )}
      <div className="invoice-sign-footer">
        <div className="signature-box">
          <div className="signature-line">Receiver's Signature</div>
        </div>
        <div className="signature-box">
          <div className="signature-line">Authorised Signatory</div>
        </div>
      </div>
    </div>
  );
}

export default function InvoiceReport() {
  const [invoiceNo, setInvoiceNo] = useState("50");
  const [d, setD] = useState(null);
  const [error, setError] = useState("");
  const [metrics, setMetrics] = useState(null);
  const [generating, setGenerating] = useState(false);
  const pagesRef = useRef(null);

  const mHeaderRef = useRef(null);
  const mMetaRef = useRef(null);
  const mTheadRef = useRef(null);
  const mRowRef = useRef(null);
  const mFooterRef = useRef(null);
  const mContinuedRef = useRef(null);

  const load = async () => {
    setError("");
    setD(null);
    setMetrics(null);
    const res = await fetch(`${API_BASE_URL}/reports/invoice?invoiceNo=${invoiceNo}`);
    if (res.ok) setD(await res.json());
    else setError("Invoice not found.");
  };

  useLayoutEffect(() => {
    if (!d) return;
    if (!mHeaderRef.current) return;
    setMetrics({
      header: mHeaderRef.current.offsetHeight,
      meta: mMetaRef.current.offsetHeight,
      thead: mTheadRef.current.offsetHeight,
      row: mRowRef.current.offsetHeight,
      footer: mFooterRef.current.offsetHeight,
      continued: mContinuedRef.current.offsetHeight,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [d]);

  const pages = useMemo(() => {
    if (!d || !metrics) return null;
    const items = d.items ?? [];
    const { header, meta, thead, row, footer, continued } = metrics;

    const capFirstNoFooter = Math.max(1, Math.floor((CONTENT_H_PX - header - meta - thead - continued) / row));
    const capFirstWithFooter = Math.max(1, Math.floor((CONTENT_H_PX - header - meta - thead - footer) / row));
    const capMidNoFooter = Math.max(1, Math.floor((CONTENT_H_PX - header - thead - continued) / row));
    const capLastWithFooter = Math.max(1, Math.floor((CONTENT_H_PX - header - thead - footer) / row));

    if (items.length === 0) {
      return [{ rows: [], startIndex: 0, isLast: true, isFirst: true }];
    }

    const result = [];
    let i = 0;
    let isFirst = true;
    while (i < items.length) {
      const remaining = items.length - i;
      const capWithFooter = isFirst ? capFirstWithFooter : capLastWithFooter;
      const capNoFooter = isFirst ? capFirstNoFooter : capMidNoFooter;

      if (remaining <= capWithFooter) {
        result.push({ rows: items.slice(i, i + remaining), startIndex: i, isLast: true, isFirst });
        i += remaining;
      } else {
        const take = Math.max(1, Math.min(capNoFooter, remaining - 1));
        result.push({ rows: items.slice(i, i + take), startIndex: i, isLast: false, isFirst });
        i += take;
      }
      isFirst = false;
    }
    return result;
  }, [d, metrics]);

  // Generates a REAL PDF (via html2canvas + jsPDF) and opens it in a new tab
  // using the browser's native PDF viewer - which has its own Print/Save/Download
  // icons and NEVER adds a page URL/date header, unlike printing the live webpage.
  const handlePrint = async () => {
    setGenerating(true);
    try {
      const pageEls = pagesRef.current.querySelectorAll(".invoice-page");
      const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "portrait" });
      for (let i = 0; i < pageEls.length; i++) {
        const canvas = await html2canvas(pageEls[i], { scale: 2, useCORS: true });
        const imgData = canvas.toDataURL("image/png");
        if (i > 0) pdf.addPage();
        pdf.addImage(imgData, "PNG", 0, 0, 210, 297);
      }
      const blobUrl = pdf.output("bloburl", { filename: `Invoice-${d.invoiceNo}.pdf` });
      window.open(blobUrl, "_blank");
    } finally {
      setGenerating(false);
    }
  };

  const sampleItem = d?.items?.[0] ?? { productName: "Sample", hsn: "0000", gstPercent: 0, qty: 1, rate: 1, amount: 1 };

  return (
    <div className="invoice-a4-wrapper">
      <style>{`
        .invoice-a4-wrapper { background: #e5e7eb; min-height: 100vh; padding: 24px 0; font-family: Arial, Helvetica, sans-serif; }
        .invoice-controls { max-width: 210mm; margin: 0 auto 16px; display: flex; justify-content: center; align-items: center; gap: 8px; }
        .invoice-controls input { padding: 8px 10px; border: 1px solid #cbd5e1; border-radius: 6px; font-size: 14px; }
        .invoice-controls button { padding: 8px 16px; border: none; border-radius: 6px; font-size: 14px; cursor: pointer; background: #2563eb; color: white; }
        .invoice-controls button.print-btn { background: #16a34a; }
        .invoice-controls button:disabled { opacity: 0.6; cursor: not-allowed; }
        .invoice-error { text-align: center; color: #dc2626; margin-bottom: 12px; }
        .invoice-hint { text-align: center; color: #6b7280; font-size: 12px; margin: -8px auto 12px; max-width: 210mm; }

        .invoice-page { width: 210mm; min-height: 297mm; margin: 0 auto 20px; background: white; box-shadow: 0 0 8px rgba(0,0,0,0.2); box-sizing: border-box; padding: 15mm 12mm; display: flex; flex-direction: column; }
        .invoice-body { display: flex; flex-direction: column; flex: 1; }
        .invoice-header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2px solid #111827; padding-bottom: 10px; margin-bottom: 14px; min-height: 60px; }
        .invoice-header.blank { min-height: 90px; border-bottom: none; }
        .invoice-company h1 { font-size: 20px; margin: 0 0 4px 0; color: #111827; }
        .invoice-company p { font-size: 11px; margin: 0; color: #4b5563; }
        .invoice-title { text-align: right; }
        .invoice-title h2 { font-size: 18px; margin: 0 0 4px 0; color: #111827; letter-spacing: 1px; }
        .invoice-title p { font-size: 11px; margin: 0; color: #4b5563; }

        .invoice-meta-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px; font-size: 12px; }
        .invoice-meta-box { border: 1px solid #d1d5db; border-radius: 4px; padding: 8px 10px; }
        .invoice-meta-box strong { display: block; font-size: 10px; text-transform: uppercase; color: #6b7280; margin-bottom: 4px; }

        table.invoice-table { width: 100%; border-collapse: collapse; font-size: 11.5px; table-layout: fixed; }
        table.invoice-table th, table.invoice-table td { border: 1px solid #d1d5db; padding: 5px 6px; overflow: hidden; }
        table.invoice-table th { background: #f3f4f6; text-align: left; font-size: 10.5px; text-transform: uppercase; color: #374151; }
        table.invoice-table td.num { text-align: right; }

        .continued-note { text-align: right; font-size: 10px; color: #6b7280; margin-top: 6px; }

        .invoice-footer-block { margin-top: auto; padding-top: 10px; }
        .tax-table { width: 260px; margin-left: auto; border-collapse: collapse; font-size: 11px; margin-top: 8px; }
        .tax-table td { border: 1px solid #d1d5db; padding: 4px 8px; }
        .tax-table td.num { text-align: right; }

        .invoice-net { text-align: right; font-size: 15px; font-weight: bold; margin-top: 8px; }
        .invoice-words { font-size: 11px; font-style: italic; margin-top: 10px; }
        .invoice-terms { font-size: 11px; margin-top: 14px; }

        .invoice-sign-footer { margin-top: 20px; display: flex; justify-content: space-between; align-items: flex-end; border-top: 1px solid #d1d5db; padding-top: 12px; }
        .invoice-sign-footer .signature-box { text-align: center; font-size: 11px; color: #4b5563; }
        .invoice-sign-footer .signature-line { width: 160px; border-top: 1px solid #9ca3af; margin-top: 40px; padding-top: 4px; }

        .measure-probe { position: fixed; top: -9999px; left: -9999px; visibility: hidden; width: 210mm; box-sizing: border-box; padding: 0 12mm; }
      `}</style>

      <div className="invoice-controls">
        <input
          placeholder="Invoice No."
          value={invoiceNo}
          onChange={(e) => setInvoiceNo(e.target.value)}
        />
        <button onClick={load}>Load Invoice</button>
        {d && (
          <button className="print-btn" onClick={handlePrint} disabled={generating}>
            {generating ? "Preparing PDF..." : "Print / Save PDF"}
          </button>
        )}
      </div>
      {d && (
        <p className="invoice-hint">
          Opens the finished invoice as a PDF in a new tab - use the Print or Download icon in that tab's own toolbar.
        </p>
      )}

      {error && <p className="invoice-error">{error}</p>}

      {d && (
        <div className="measure-probe" aria-hidden="true">
          <div ref={mHeaderRef}><HeaderBlock d={d} /></div>
          <div ref={mMetaRef}><MetaBlock d={d} /></div>
          <table className="invoice-table" ref={mTheadRef}><TableHead /></table>
          <table className="invoice-table"><tbody ref={mRowRef}><ItemRow item={sampleItem} sr={1} /></tbody></table>
          <div ref={mFooterRef}><FooterBlock d={d} /></div>
          <p className="continued-note" ref={mContinuedRef}>Continued on next page...</p>
        </div>
      )}

      {d && pages && (
        <div ref={pagesRef}>
          {pages.map((page, pageIdx) => (
            <div className="invoice-page" key={pageIdx}>
              <div className="invoice-body">
                <HeaderBlock d={d} />
                {page.isFirst && <MetaBlock d={d} />}

                <table className="invoice-table">
                  <TableHead />
                  <tbody>
                    {page.rows.map((item, i) => (
                      <ItemRow key={i} item={item} sr={page.startIndex + i + 1} />
                    ))}
                  </tbody>
                </table>

                {!page.isLast && <p className="continued-note">Continued on next page...</p>}
                {page.isLast && <FooterBlock d={d} />}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}